import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, Check, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function DocsQuickStart() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const loaderSnippet = `<script>
  (function(w, d, s, src, name) {
    if (w[name]) { return; }
    var ci = w[name] = function() { ci.q.push(arguments); };
    ci.q = [];
    var el = d.createElement(s);
    el.async = true;
    el.src = src;
    var firstScript = d.getElementsByTagName(s)[0];
    firstScript.parentNode.insertBefore(el, firstScript);
  })(window, document, 'script', 'https://cdn.commerceindex.com/ci.js', 'ci');

  ci('init', { brandId: 'YOUR_BRAND_ID' });
</script>`;

  const trackViewSnippet = `ci('trackView', {
  productId: 'prod_123',
  productSku: 'SKU-123',
  productTitle: 'Product Name',
  productUrl: 'https://brand.com/products/product-name'
});`;

  const trackPurchaseSnippet = `ci('trackPurchase', {
  orderId: 'order_789',
  currency: 'USD',
  items: [
    {
      productId: 'prod_123',
      productSku: 'SKU-123',
      productTitle: 'Product Name',
      quantity: 2,
      value: 39.99
    }
  ]
});`;

  const steps = [
    {
      number: 1,
      title: 'Get your Brand ID and API Key',
      description: 'We\'ll provide your brandId and X-CI-Api-Key during onboarding.',
      content: (
        <div className="p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
          <p className="text-sm text-[#94A3B8]">
            <strong className="text-[#FFD166]">Note:</strong> Contact us at{' '}
            <a href="mailto:integrations@commerceindex.com" className="text-[#3EF1C7] hover:underline">
              integrations@commerceindex.com
            </a>
            {' '}to get your credentials.
          </p>
        </div>
      )
    },
    {
      number: 2,
      title: 'Install CI Loader Script',
      description: 'Add this snippet to your site\'s layout, before the closing </body> tag.',
      code: loaderSnippet,
      codeId: 'loader'
    },
    {
      number: 3,
      title: 'Track Product Views',
      description: 'On each product detail page (PDP), call trackView with product information.',
      code: trackViewSnippet,
      codeId: 'trackView'
    },
    {
      number: 4,
      title: 'Track Purchases',
      description: 'On the order confirmation page, call trackPurchase with order details.',
      code: trackPurchaseSnippet,
      codeId: 'trackPurchase'
    },
    {
      number: 5,
      title: 'Verify Events',
      description: 'Check your browser\'s Network tab to confirm events are being sent.',
      content: (
        <div className="space-y-3">
          <p className="text-sm text-[#94A3B8]">
            Open DevTools → Network tab → Filter by "collect" to see CI events.
          </p>
          <div className="p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
            <p className="text-sm text-[#94A3B8]">
              Once integrated, contact us to confirm events are being received and enable
              Proof on your product pages.
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Getting Started
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Quick Start
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Get up and running with Commerce Index Proof in under 30 minutes.
        </p>
      </div>

      {/* Timeline */}
      <div className="space-y-0">
        {steps.map((step, index) => (
          <div key={step.number} className="relative">
            {/* Timeline line */}
            {index < steps.length - 1 && (
              <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-[#334155]" />
            )}
            
            <div className="flex gap-6 pb-8">
              {/* Step number */}
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#3EF1C7] flex items-center justify-center text-[#0B1120] font-bold relative z-10">
                {step.number}
              </div>
              
              {/* Step content */}
              <div className="flex-1 pt-1">
                <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-[#94A3B8] mb-4">{step.description}</p>
                
                {step.code && (
                  <div className="relative">
                    <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                      <code>{step.code}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                      onClick={() => copyToClipboard(step.code, step.codeId)}
                      data-testid={`copy-${step.codeId}`}
                    >
                      {copiedCode === step.codeId ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
                
                {step.content}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Next Steps */}
      <Card className="bg-[#0D1628] border-[#22304A] mt-8">
        <CardHeader>
          <CardTitle className="text-white">What's Next?</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {[
              { text: 'Learn about event models', href: '/docs/events' },
              { text: 'Explore the JavaScript API', href: '/docs/js-api' },
              { text: 'See platform-specific examples', href: '/docs/examples' },
            ].map((item, i) => (
              <li key={i}>
                <a 
                  href={item.href}
                  className="flex items-center gap-2 text-[#3EF1C7] hover:underline"
                >
                  <ChevronRight className="w-4 h-4" />
                  {item.text}
                </a>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
